import { useState } from "react";
import { X, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CHANNELS, FU_STATUS, FU_STATUS_LABELS, wibLocalToUtcIso } from "@/lib/prospectMeta";

const inputCls = "w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:border-[#0f2847] focus:ring-2 focus:ring-[#0f2847]/20 focus:outline-none";

export function FollowUpForm({ prospects = [], fixedProspectId = "", members = [], defaultPic = "", onClose, onSaved }) {
  const [form, setForm] = useState({
    prospect_id: fixedProspectId,
    scheduled_at: "",
    channel: "WhatsApp",
    outcome: "",
    notes: "",
    next_follow_up_at: "",
    status: "terjadwal",
    pic_user_id: defaultPic,
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.prospect_id) { toast.error("Pilih prospek terlebih dahulu."); return; }
    setSaving(true);
    try {
      await api.post(`/prospects/${form.prospect_id}/followups`, {
        scheduled_at: wibLocalToUtcIso(form.scheduled_at),
        channel: form.channel,
        outcome: form.outcome,
        notes: form.notes,
        next_follow_up_at: wibLocalToUtcIso(form.next_follow_up_at),
        status: form.status,
        pic_user_id: form.pic_user_id,
      });
      toast.success("Follow-up ditambahkan.");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal menyimpan.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="followup-modal">
      <div className="bg-white rounded-xl w-full max-w-lg mt-8 shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-900">Tambah Follow-up</h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid="followup-modal-close"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3" data-testid="followup-modal-form">
          {!fixedProspectId && (
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Prospek</label>
              <select className={inputCls} value={form.prospect_id} onChange={set("prospect_id")} data-testid="fu-prospect" required>
                <option value="">Pilih prospek…</option>
                {prospects.map((p) => <option key={p.id} value={p.id}>{p.institution_name || p.full_name}</option>)}
              </select>
            </div>
          )}
          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Tanggal & Jam</label><input type="datetime-local" className={inputCls} value={form.scheduled_at} onChange={set("scheduled_at")} data-testid="fu-scheduled" required /></div>
          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Kanal</label><select className={inputCls} value={form.channel} onChange={set("channel")} data-testid="fu-channel">{CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Hasil Komunikasi</label><input className={inputCls} value={form.outcome} onChange={set("outcome")} data-testid="fu-outcome" /></div>
          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Catatan</label><textarea className={inputCls} rows={2} value={form.notes} onChange={set("notes")} data-testid="fu-notes" /></div>
          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Follow-up Berikutnya</label><input type="datetime-local" className={inputCls} value={form.next_follow_up_at} onChange={set("next_follow_up_at")} data-testid="fu-next" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Status</label><select className={inputCls} value={form.status} onChange={set("status")} data-testid="fu-status">{FU_STATUS.map((s) => <option key={s} value={s}>{FU_STATUS_LABELS[s]}</option>)}</select></div>
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">PIC</label><select className={inputCls} value={form.pic_user_id} onChange={set("pic_user_id")} data-testid="fu-pic">{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50" data-testid="fu-cancel">Batal</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-[#0f2847] hover:bg-[#17416f] text-white text-sm font-medium px-4 py-2 rounded-lg" data-testid="fu-modal-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Simpan</button>
          </div>
        </form>
      </div>
    </div>
  );
}
